import { useEffect, useState } from 'react'
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import type { TrackDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Track extends TrackDoc {
  id: string
}

/** Every circuit, by name. Small enough to hold whole — there are a few dozen
 *  and every event page needs one, so paging would cost more than it saved. */
export function useTracks() {
  const [tracks, setTracks] = useState<Track[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'tracks'), orderBy('name')),
      (snap) => setTracks(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TrackDoc) }))),
      (e) => setError(e.message),
    )
  }, [])

  return { tracks, error }
}

export interface TrackInput {
  name: string
  location: { lat: number; lng: number } | null
  lengthM: number | null
  timezone: string
  country: string | null
}

/**
 * Creates a circuit at a chosen id.
 *
 * setDoc at a derived id rather than addDoc, because the id is the thing events
 * reference and the scraper has to be able to arrive at the same one
 * independently. A generated id could not be re-derived from a name.
 */
export async function createTrack(id: string, input: TrackInput) {
  return setDoc(doc(db, 'tracks', id), {
    ...input,
    vendorNames: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

export async function updateTrack(id: string, input: TrackInput) {
  return updateDoc(doc(db, 'tracks', id), { ...input, updatedAt: serverTimestamp() })
}

/** How many events are held at this circuit. See countSessionsForSeries for
 *  why this is a count rather than the documents. */
export async function countEventsForTrack(trackId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(db, 'events'), where('trackId', '==', trackId)),
  )
  return snap.data().count
}

/**
 * Deletes a circuit.
 *
 * Note the scrape will recreate it as a stub the next time TSL names it, which
 * is correct — deleting a circuit TSL still runs at is a statement about our
 * records, not about the world. What is lost is the location and length, so the
 * confirmation says so.
 */
export async function deleteTrack(id: string) {
  return deleteDoc(doc(db, 'tracks', id))
}
